import { useEffect, useState } from "react";
import { Banner } from "../common/Banner";
import { fusionCompanies, getAllCompanies } from "../../services/companiesService";
import Swal from "sweetalert2";
export default function FusionCompanies({ isSubmitting }) {
  const [companies, setCompanies] = useState([]);
  const [company1, setCompany1] = useState(null);
  const [company2, setCompany2] = useState(null);
  const [searchTerm1, setSearchTerm1] = useState("");
  const [searchTerm2, setSearchTerm2] = useState("");
  const [showDropdown1, setShowDropdown1] = useState(false);
  const [showDropdown2, setShowDropdown2] = useState(false);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getAllCompanies();
        setCompanies(response);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  const isValid =
    company1?.status === "Active" &&
    company2?.status === "Active" &&
    company1?.periodicity === company2?.periodicity &&
    company1?.rut !== company2?.rut;

  const getFusionErrorMessage = () => {
    if (!company1 || !company2) return "Debes seleccionar dos compañías.";
    if (company1.rut === company2.rut) return "Las compañías seleccionadas deben ser diferentes.";
    if (company1.status !== "Active" || company2.status !== "Active") return "Ambas compañías deben estar activas para poder fusionarlas.";
    if (company1.periodicity !== company2.periodicity) return "Las compañías deben tener la misma periodicidad.";
    return "";
  };

  const handleFusion = async () => {
    if (!isValid) return;
    const confirmation = await Swal.fire({
      title: "¿Estás seguro?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, fusionar",
      cancelButtonText: "Cancelar",
    });
    if (!confirmation.isConfirmed) return;
    isSubmitting(true);
    try {
      const response = await fusionCompanies({
        companyFromRut: company1.rut,
        companyToRut: company2.rut,
      });
      isSubmitting(false);
      if (response.success) {
        Swal.fire({
          title: "Fusión exitosa",
          text: "Las compañías han sido fusionadas correctamente.",
          icon: "success",
        });
        setCompany1(null);
        setCompany2(null);
        setSearchTerm1("");
        setSearchTerm2("");
      }
    } catch (error) {
      console.error("Error during fusion:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo completar la fusión.",
        icon: "error",
      });
    } finally {
      isSubmitting(false);  
    }
  };

  const filterCompanies = (searchTerm) => {
    return companies.filter(company =>
      company.business_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.trade_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.rut.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleCompanySelect = (company, isCompany1) => {
    if (isCompany1) {
      setCompany1(company);
      setSearchTerm1(company.business_name);
      setShowDropdown1(false);
    } else {
      setCompany2(company);
      setSearchTerm2(company.business_name);
      setShowDropdown2(false);
    }
  };

  //TRADUCIR STATUS Y  DEFINIR COLOR POR STATUS
  const translateStatus = (status) => {
    switch (status) {
      case "Active":
        return { text: "Activo", color: "text-green-600" };
      case "Inactive":
        return { text: "Inactivo", color: "text-red-600" };
      case "Merged":
        return { text: "Fusionado", color: "text-yellow-600" };
      case "Suspended":
        return { text: "Suspendido", color: "text-orange-600" };
      default:
        return { text: status, color: "text-gray-600" };
    }
  };

  const renderSearchableSelect = (searchTerm, setSearchTerm, showDropdown, setShowDropdown, selectedCompany, isCompany1) => {
    const filteredCompanies = filterCompanies(searchTerm).filter(company => company.role !== "admin");
    
    return (
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder="Buscar compañía por nombre o RUT..."
          className="w-full border border-gray-300 px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0488AF] focus:border-transparent"
        />
        
        {showDropdown && searchTerm && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {filteredCompanies.length > 0 ? (
              filteredCompanies.map((company) => (
                <div
                  key={company.rut}
                  onClick={() => handleCompanySelect(company, isCompany1)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{company.business_name}</div>
                  <div className="text-sm text-gray-600">{company.trade_name} - {company.rut}</div>
                  <div className={`text-xs font-semibold ${translateStatus(company.status).color}`}>
                    {translateStatus(company.status).text}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-sm">No se encontraron compañías</div>
            )}
          </div>
        )}
        
        {selectedCompany && searchTerm && (
          <button
            onClick={() => {
              if (isCompany1) {
                setCompany1(null);
                setSearchTerm1("");
              } else {
                setCompany2(null);
                setSearchTerm2("");
              }
            }}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
    );
  };

  const renderCard = (company) => {
    if (!company) {
      return (
        <div className="h-64 mt-4 p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
          <p className="text-gray-500 text-center">
            Selecciona una compañía para ver sus detalles
          </p>
        </div>
      );
    }

    const isMergeable = company.status === "Active";
    return (
      <div className={`h-64 mt-4 p-4 rounded-lg shadow-lg transition-all duration-200 ${
        isMergeable
          ? "bg-gradient-to-br from-[#E6F7FB] to-[#F0FAFE] border-2 border-[#0488AF] hover:shadow-xl hover:shadow-[#0488AF]/20"
          : "bg-gradient-to-br from-[#FDE6ED] to-[#FEF0F5] border-2 border-[#DC3465] hover:shadow-xl hover:shadow-[#DC3465]/20"
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-1 line-clamp-2">{company.business_name}</h3>
            <p className="text-sm font-semibold text-gray-700 mb-3 line-clamp-1">{company.trade_name}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-600">Estado:</span>
                <span className={`font-bold px-2 py-1 rounded-full text-xs ${
                  company.status === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {company.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-600">RUT:</span>
                <span className="text-gray-900 font-mono text-xs">{company.rut}</span>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-2 mt-2">
            <div className="text-xs text-gray-600 space-y-1">
              <div className="flex justify-between">
                <span>Email:</span>
                <span className="truncate ml-2 max-w-32">{company.contact_email}</span>
              </div>
              <div className="flex justify-between">
                <span>Cuota:</span>
                <span className="font-semibold">{company.amount}</span>
              </div>
              <div className="flex justify-between">
                <span>Periodicidad:</span>
                <span>{company.periodicity}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Cerrar dropdowns al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowDropdown1(false);
        setShowDropdown2(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Banner
        title="Fusión de socios"
        message="Selecciona dos compañías activas para fusionarlas."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Socio 1 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center">
            <span className="bg-[#0488AF] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">1</span>
            Primer Socio
          </h2>
          {renderSearchableSelect(
            searchTerm1, 
            setSearchTerm1, 
            showDropdown1, 
            setShowDropdown1, 
            company1, 
            true
          )}
          {renderCard(company1)}
        </div>

        {/* Socio 2 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center">
            <span className="bg-[#0488AF] text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">2</span>
            Segundo Socio
          </h2>
          {renderSearchableSelect(
            searchTerm2, 
            setSearchTerm2, 
            showDropdown2, 
            setShowDropdown2, 
            company2, 
            false
          )}
          {renderCard(company2)}
        </div>
      </div>

      {/* Mensaje de error */}
      {!isValid && (company1 || company2) && (
        <div className="mt-6 max-w-2xl mx-auto">
          <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-semibold text-red-800">Error de validación</h4>
                <p className="text-sm text-red-700 mt-1">{getFusionErrorMessage()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón de fusión */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleFusion}
          disabled={!isValid}
          className={`px-8 py-3 text-white font-bold rounded-lg transition-all duration-200 transform ${
            isValid
              ? "bg-gradient-to-r from-[#1BBB55] to-[#16A34A] hover:from-[#1aab51] hover:to-[#15803d] hover:scale-105 shadow-lg hover:shadow-xl"
              : "bg-gray-400 cursor-not-allowed opacity-60"
          }`}
        >
          {isValid ? "🔗 Fusionar Compañías" : "Fusionar Compañías"}
        </button>
      </div>
    </div>
  );
}